// Batch 0 (UX/IA overhaul) — the audit / chain-of-custody trail renderer.
//
// The audit's most concrete BFSI/STQC gap: the backend already persists
// `created_by`/`acknowledged_by`/`resolved_by`/`exported_by` + timestamps +
// IP for alerts, recording gaps, evidence, and case items — but NO screen
// shows them. This renders those events uniformly as a "who did what, when
// (and from where)" trail so the trail is visible to an operator or auditor.
// Batch 1 wires the real fields in; Batch 0 provides the consistent surface.
import { ShieldCheckIcon } from "lucide-react";

import { Timestamp } from "@/components/ui/timestamp";
import type { TimestampInput } from "@/utils/datetime";
import { cn } from "@/lib/utils";

export interface CustodyEvent {
  /** e.g. "Created", "Acknowledged", "Resolved", "Exported", "Added to case". */
  action: string;
  /** Resolved actor name (use useResolveNames upstream — never a raw UUID). */
  actor?: string | null;
  at?: TimestampInput;
  /** Optional source IP (evidence export / access). */
  ip?: string | null;
  /** Optional free-text reason (gap ack reason, hold reason). */
  reason?: string | null;
}

export interface CustodyPanelProps {
  title?: string;
  events: CustodyEvent[];
  /** Optional integrity hash / checksum to display for evidence items. */
  integrityHash?: string | null;
  className?: string;
  emptyText?: string;
}

export function CustodyPanel({
  title = "Chain of custody",
  events,
  integrityHash,
  className,
  emptyText = "No custody events recorded yet.",
}: CustodyPanelProps) {
  return (
    <div className={cn("space-y-3", className)}>
      <div className="flex items-center gap-2 text-sm font-medium">
        <ShieldCheckIcon className="h-4 w-4 text-muted-foreground" />
        {title}
      </div>

      {events.length === 0 ? (
        <p className="text-xs text-muted-foreground">{emptyText}</p>
      ) : (
        <ol className="space-y-2 border-l pl-4">
          {events.map((e, i) => (
            <li key={i} className="relative text-xs">
              <span
                className="absolute -left-[1.3rem] top-1 h-2 w-2 rounded-full bg-primary/70"
                aria-hidden
              />
              <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                <span className="font-medium text-foreground">{e.action}</span>
                {e.actor && <span className="text-muted-foreground">by {e.actor}</span>}
                {e.at != null && (
                  <span className="text-muted-foreground">
                    · <Timestamp value={e.at} />
                  </span>
                )}
                {e.ip && <span className="text-muted-foreground">· from {e.ip}</span>}
              </div>
              {e.reason && (
                <div className="mt-0.5 italic text-muted-foreground">“{e.reason}”</div>
              )}
            </li>
          ))}
        </ol>
      )}

      {integrityHash && (
        <div className="rounded border bg-muted/40 px-3 py-2 font-mono text-[11px] text-muted-foreground">
          <span className="font-sans font-medium text-foreground">Integrity: </span>
          <span className="break-all">{integrityHash}</span>
        </div>
      )}
    </div>
  );
}
